import { Box, Text } from '@chakra-ui/react';
import { memo, useCallback, useEffect, useState } from 'react';
import { CalendarIcon, dateFromString } from 'ui-components';
import { Button } from 'ui-components/dist/Button';
import { ChevronDownIcon } from 'ui-components/dist/ChevronDownIcon';
import {
  Dropdown,
  DropdownContent,
  DropdownItem,
} from 'ui-components/dist/Dropdown';
import { PostedAtDateTimePickerModal } from '~/components/ContentSearchPostedAtTimeframe/PostedAtDateTimePickerModal';
import { MONTH_NAME_DAY_YEAR_FORMAT } from '~/constants';
import { formatDate } from '~/utils';

interface IContentSearchPostedAtTimeframeProps {
  postedBefore: string | null;
  postedAfter: string | null;
  onFilter: (postedAfter?: string, postedBefore?: string) => void;
  trackId?: string;
}

export enum Timeframe {
  Anytime = 'anytime',
  LastDay = '1d',
  Last7Days = '7d',
  Last30Days = '30d',
  Last90Days = '90d',
  LastYear = '1y',
  Custom = 'custom',
}

const timeFrameToText = {
  [Timeframe.Anytime]: 'Anytime',
  [Timeframe.LastDay]: 'Last day',
  [Timeframe.Last7Days]: 'Last 7 days',
  [Timeframe.Last30Days]: 'Last 30 days',
  [Timeframe.Last90Days]: 'Last 90 days',
  [Timeframe.LastYear]: 'Last year',
  [Timeframe.Custom]: 'Custom',
};

export const timeFrameToDays = {
  [Timeframe.LastDay]: 1,
  [Timeframe.Last7Days]: 7,
  [Timeframe.Last30Days]: 30,
  [Timeframe.Last90Days]: 90,
  [Timeframe.LastYear]: 365,
};

export const ContentSearchPostedAtTimeframe: React.FC<IContentSearchPostedAtTimeframeProps> =
  memo(({ postedBefore, postedAfter, onFilter, trackId }) => {
    const [timeframeText, setTimeframeText] = useState<string>(
      timeFrameToText[Timeframe.Anytime],
    );

    const [isModalOpen, setIsModalOpen] = useState<boolean>(false);

    const parseTimeframeText = useCallback(
      (postedAfter: string | null, postedBefore: string | null) => {
        if (postedAfter && postedBefore) {
          return (
            formatDate(
              dateFromString(postedAfter) ?? postedAfter,
              MONTH_NAME_DAY_YEAR_FORMAT,
            ) +
            ' - ' +
            formatDate(
              dateFromString(postedBefore) ?? postedBefore,
              MONTH_NAME_DAY_YEAR_FORMAT,
            )
          );
        }

        if (postedBefore) {
          return `Before ${formatDate(
            dateFromString(postedBefore) ?? postedBefore,
            MONTH_NAME_DAY_YEAR_FORMAT,
          )}`;
        }

        if (postedAfter) {
          if (postedAfter in timeFrameToDays) {
            return timeFrameToText[postedAfter as Timeframe];
          }

          return `After ${formatDate(
            dateFromString(postedAfter) ?? postedAfter,
            MONTH_NAME_DAY_YEAR_FORMAT,
          )}`;
        }

        return;
      },
      [],
    );

    const handleDropdownItemClick = useCallback(
      (key: Timeframe) => {
        if (key === Timeframe.Custom) {
          setIsModalOpen(true);
        } else if (key === Timeframe.Anytime) {
          onFilter();
        } else {
          onFilter(key);
        }
      },
      [onFilter, setIsModalOpen],
    );

    useEffect(() => {
      const parsedText = parseTimeframeText(postedAfter, postedBefore);
      setTimeframeText(parsedText ?? timeFrameToText[Timeframe.Anytime]);
    }, [postedBefore, postedAfter, parseTimeframeText]);

    return (
      <>
        <Dropdown>
          {({ buttonRef, getReferenceProps }) => (
            <Box position="relative" display="flex-inline">
              <Button
                variant="tertiary"
                size="md"
                {...getReferenceProps()}
                ref={buttonRef}
                trackId={`${trackId}-drop-down`}
                leftIcon={<CalendarIcon boxSize={4} />}
                rightIcon={
                  <Box display={{ base: 'none', md: 'inline-block' }}>
                    <ChevronDownIcon boxSize={4} />
                  </Box>
                }
              >
                <Text
                  display={{ base: 'none', md: 'inline' }}
                  fontWeight="300"
                  data-testid="postedAtFilterText"
                >
                  Posted: {timeframeText}
                </Text>
                <Text display={{ base: 'inline', md: 'none' }} srOnly>
                  Posted: {timeframeText}
                </Text>
              </Button>

              <DropdownContent minHeight={250}>
                <DropdownItem
                  key={Timeframe.Anytime}
                  onClick={() => handleDropdownItemClick(Timeframe.Anytime)}
                >
                  {timeFrameToText[Timeframe.Anytime]}
                </DropdownItem>
                {Object.entries(timeFrameToText)
                  .filter(([key]) => key !== Timeframe.Anytime)
                  .map(([key, text]) => (
                    <DropdownItem
                      key={key}
                      onClick={() => handleDropdownItemClick(key as Timeframe)}
                    >
                      {text}
                    </DropdownItem>
                  ))}
              </DropdownContent>
            </Box>
          )}
        </Dropdown>
        <PostedAtDateTimePickerModal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          onFilter={onFilter}
          postedBefore={postedBefore}
          postedAfter={postedAfter}
        />
      </>
    );
  });
